import { getEntitySource } from "building-generation/buildingGraphFilter/entityFilterSourceImpl";
import type { FilterEntitySourceId } from "building-types/buildingGraphFilter/entitySource";
import type { PropertyDescriptor, PropertyPath } from "building-types/buildingGraphFilter/propertyCatalog";
import type {
  DocBuildingGraphFilterGraph,
  DocBuildingGraphFilterNode,
  DocBuildingGraphPredicateFilterNode,
  DocBuildingGraphSubfilterValueSource,
} from "building-types/walls2/wallPlaneFilters";
import { assertIsNotNullOrUndefined } from "common/utils/assertOfType";
import { assertUnreachable } from "common/utils/assertUnreachable";

export interface TraverseDocFilterGraphEvaluateNodeArgs<T> {
  readonly graphId: string;
  readonly nodeId: string;
  readonly node: DocBuildingGraphFilterNode;
  readonly childResult?: T;
  readonly childResults?: readonly T[];
}

export type TraverseDocFilterGraphEvaluateNode<T> = (args: TraverseDocFilterGraphEvaluateNodeArgs<T>) => T;

export function validateDocFilterGraph(
  filterGraphs: Record<string, DocBuildingGraphFilterGraph>,
  graphId: string,
  expectedTargetType: FilterEntitySourceId,
  operationId: string,
): void {
  traverseDocFilterGraph(filterGraphs, graphId, expectedTargetType, operationId);
}

export function traverseDocFilterGraph<T>(
  filterGraphs: Record<string, DocBuildingGraphFilterGraph>,
  graphId: string,
  expectedTargetType: FilterEntitySourceId,
  operationId: string,
  evaluateNode?: TraverseDocFilterGraphEvaluateNode<T>,
): T | undefined {
  return traverseGraph(filterGraphs, graphId, expectedTargetType, operationId, new Set<string>(), evaluateNode);
}

function traverseGraph<T>(
  filterGraphs: Record<string, DocBuildingGraphFilterGraph>,
  graphId: string,
  expectedTargetType: FilterEntitySourceId,
  operationId: string,
  activeNodeIds: Set<string>,
  evaluateNode?: TraverseDocFilterGraphEvaluateNode<T>,
): T | undefined {
  const graph = filterGraphs[graphId];
  if (!graph) {
    throw new Error(`WallPlaneOperation "${operationId}" references missing filter graph "${graphId}"`);
  }
  const rootNode = graph.nodes[graph.rootNodeId];
  if (rootNode?.filterType === "NOT") {
    throw new Error(
      "NOT may only appear as a child of AND with at least one positive sibling; standalone NOT is not supported",
    );
  }
  return traverseNode(
    filterGraphs,
    graphId,
    graph.rootNodeId,
    expectedTargetType,
    operationId,
    activeNodeIds,
    evaluateNode,
  );
}

function traverseNode<T>(
  filterGraphs: Record<string, DocBuildingGraphFilterGraph>,
  graphId: string,
  nodeId: string,
  expectedTargetType: FilterEntitySourceId,
  operationId: string,
  activeNodeIds: Set<string>,
  evaluateNode?: TraverseDocFilterGraphEvaluateNode<T>,
): T | undefined {
  const graph = filterGraphs[graphId];
  if (!graph) {
    throw new Error(`WallPlaneOperation "${operationId}" references missing filter graph "${graphId}"`);
  }
  const node = graph.nodes[nodeId];
  if (!node) {
    throw new Error(`WallPlaneOperation "${operationId}" references missing filter node "${nodeId}"`);
  }
  const stackKey = `${graphId}:${nodeId}`;
  if (activeNodeIds.has(stackKey)) {
    throw new Error(`WallPlaneOperation "${operationId}" contains a filter cycle at "${stackKey}"`);
  }
  activeNodeIds.add(stackKey);
  try {
    assertNodeTargetType(node, expectedTargetType, operationId, nodeId);
    switch (node.filterType) {
      case "AND":
      case "OR": {
        if (!evaluateNode) {
          for (const childNodeId of node.childrenNodeIds) {
            traverseNode(
              filterGraphs,
              graphId,
              childNodeId,
              expectedTargetType,
              operationId,
              activeNodeIds,
              evaluateNode,
            );
          }
          return undefined;
        }
        const childResults: T[] = node.childrenNodeIds.map((childNodeId) => {
          const childResult = traverseNode(
            filterGraphs,
            graphId,
            childNodeId,
            expectedTargetType,
            operationId,
            activeNodeIds,
            evaluateNode,
          );
          assertIsNotNullOrUndefined(childResult);
          return childResult;
        });
        return evaluateNode?.({
          graphId,
          nodeId,
          node,
          childResults,
        });
      }
      case "NOT": {
        if (!evaluateNode) {
          traverseNode(
            filterGraphs,
            graphId,
            node.childNodeId,
            expectedTargetType,
            operationId,
            activeNodeIds,
            evaluateNode,
          );
          return undefined;
        }
        const childResult = traverseNode(
          filterGraphs,
          graphId,
          node.childNodeId,
          expectedTargetType,
          operationId,
          activeNodeIds,
          evaluateNode,
        );
        assertIsNotNullOrUndefined(childResult);
        return evaluateNode?.({
          graphId,
          nodeId,
          node,
          childResult,
        });
      }
      case "PREDICATE": {
        validatePredicate(filterGraphs, node, expectedTargetType, operationId, nodeId, activeNodeIds);
        return evaluateNode?.({
          graphId,
          nodeId,
          node,
        });
      }
      default:
        assertUnreachable(node);
    }
  } finally {
    activeNodeIds.delete(stackKey);
  }
}

function validatePredicate(
  filterGraphs: Record<string, DocBuildingGraphFilterGraph>,
  node: DocBuildingGraphPredicateFilterNode,
  expectedTargetType: FilterEntitySourceId,
  operationId: string,
  nodeId: string,
  activeNodeIds: Set<string>,
): void {
  const predicateDescriptor = validatePath(expectedTargetType, node.path, operationId, nodeId);
  if (node.values !== undefined && node.valueSource !== undefined) {
    throw new Error(
      `WallPlaneOperation "${operationId}" filter node "${nodeId}" cannot provide both values and valueSource`,
    );
  }
  if (node.valueSource) {
    validateValueSource(filterGraphs, node.valueSource, predicateDescriptor, operationId, nodeId, activeNodeIds);
  }
}

function validateValueSource(
  filterGraphs: Record<string, DocBuildingGraphFilterGraph>,
  valueSource: DocBuildingGraphSubfilterValueSource,
  predicateDescriptor: PropertyDescriptor,
  operationId: string,
  nodeId: string,
  activeNodeIds: Set<string>,
): void {
  if (valueSource.selectPaths.length === 0) {
    throw new Error(
      `WallPlaneOperation "${operationId}" valueSource for "${valueSource.sourceType}" must include at least one selectPath`,
    );
  }
  const source = getEntitySource(valueSource.sourceType);
  const selectDescriptors: PropertyDescriptor[] = [];
  for (const path of valueSource.selectPaths) {
    const typedPath = path as PropertyPath<typeof valueSource.sourceType>;
    const descriptor = source.descriptor(typedPath);
    if (!descriptor) {
      throw new Error(
        `WallPlaneOperation "${operationId}" valueSource has invalid selectPath "${path}" for "${valueSource.sourceType}"`,
      );
    }
    selectDescriptors.push(descriptor);
  }
  for (const descriptor of selectDescriptors) {
    if (descriptor.type !== predicateDescriptor.type) {
      throw new Error(
        `WallPlaneOperation "${operationId}" filter node "${nodeId}" compares "${predicateDescriptor.type}" values against subfilter "${descriptor.type}" values`,
      );
    }
  }
  if (valueSource.filterGraphId) {
    traverseGraph(filterGraphs, valueSource.filterGraphId, valueSource.sourceType, operationId, activeNodeIds);
  }
}

function validatePath(
  sourceType: FilterEntitySourceId,
  path: string,
  operationId: string,
  nodeId: string,
): PropertyDescriptor {
  const source = getEntitySource(sourceType);
  const descriptor = source.descriptor(path as PropertyPath<typeof sourceType>);
  if (!descriptor) {
    throw new Error(
      `WallPlaneOperation "${operationId}" filter node "${nodeId}" has invalid path "${path}" for "${sourceType}"`,
    );
  }
  return descriptor;
}

function assertNodeTargetType(
  node: DocBuildingGraphFilterNode,
  expectedTargetType: FilterEntitySourceId,
  operationId: string,
  nodeId: string,
): void {
  if (node.targetType !== expectedTargetType) {
    throw new Error(
      `WallPlaneOperation "${operationId}" filter node "${nodeId}" targetType "${node.targetType}" does not match "${expectedTargetType}"`,
    );
  }
}
